import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Compass as CompassIcon, Lock, Shield, CreditCard, Heart } from "lucide-react";
import SocialIcons from "./SocialIcons";

/**
 * Site-wide footer with:
 *  - Brand mark
 *  - Trust strip (GDPR · Stripe · £1.99/mo)
 *  - Grouped compliance + product links
 */
export default function Footer() {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer className="border-t hairline bg-white mt-12" data-testid="site-footer">
      {/* Trust strip */}
      <div
        className="border-b hairline bg-[#111] text-white/85 px-6 lg:px-10 py-3 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-[11px] uppercase tracking-wider mono"
        data-testid="trust-strip"
      >
        <span className="flex items-center gap-1.5">
          <Shield className="w-3 h-3 text-[#ff9260]" strokeWidth={2} /> {t("footer.trustGdpr")}
        </span>
        <span className="flex items-center gap-1.5">
          <Lock className="w-3 h-3 text-[#ff9260]" strokeWidth={2} /> {t("footer.trustStripe")}
        </span>
        <span className="flex items-center gap-1.5">
          <CreditCard className="w-3 h-3 text-[#ff9260]" strokeWidth={2} /> {t("footer.trustPrice")}
        </span>
        <span className="flex items-center gap-1.5">
          <Heart className="w-3 h-3 text-[#ff9260] fill-[#ff9260]" strokeWidth={2} /> {t("footer.trustKindness")}
        </span>
      </div>

      {/* Grid of links */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 grid gap-10 md:grid-cols-[1.2fr_1fr_1fr_1fr]">
        <div>
          <div className="font-display text-2xl flex items-center gap-2 mb-3">
            <CompassIcon className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} /> CampCompass UK
          </div>
          <p className="text-xs text-[#555] leading-relaxed max-w-xs">
            {t("footer.tagline")}
          </p>
          <div className="mt-4">
            <div className="eyebrow mb-2">{t("footer.followUs")}</div>
            <SocialIcons testIdPrefix="footer-social" />
          </div>
        </div>
        <FooterGroup
          title={t("footer.product")}
          links={[
            { to: "/browse", label: t("footer.browseCampsites") },
            { to: "/wall", label: t("footer.tripWall") },
            { to: "/compare", label: t("footer.compare") },
            { to: "/community", label: t("footer.communityLink") },
            { to: "/festivals", label: "Festivals 2026" },
            { to: "/charities", label: "Charities we support" },
            { to: "/pro", label: t("footer.campcompassPro") },
            { to: "/about", label: t("footer.about") },
          ]}
        />
        <FooterGroup
          title={t("footer.legal")}
          links={[
            { to: "/terms", label: t("footer.terms") },
            { to: "/privacy", label: t("footer.privacy") },
            { to: "/cookies", label: t("footer.cookies") },
            { to: "/accessibility", label: t("footer.accessibility") },
          ]}
        />
        <FooterGroup
          title={t("footer.community")}
          links={[
            { to: "/guidelines", label: t("footer.campfireCode") },
            { href: "mailto:hello@camp-compass.uk", label: t("footer.contactUs") },
            { href: "mailto:dpo@camp-compass.uk", label: t("footer.dataRequests") },
            { href: "mailto:security@camp-compass.uk", label: t("footer.security") },
          ]}
        />
      </div>

      <div className="border-t hairline px-6 lg:px-10 py-5 text-xs mono text-[#888] flex flex-col md:flex-row items-start md:items-center justify-between gap-3 max-w-[1400px] mx-auto">
        <div>
          {t("footer.copyright", { year })}
          <span className="block md:inline md:ml-3 text-[#aaa]">
            · ICO reference: <span className="text-[#666]" data-testid="footer-ico-number">CSN5693592</span>
          </span>
        </div>
        <div>{t("footer.craftedFor")}</div>
      </div>
    </footer>
  );
}

const FooterGroup = ({ title, links }) => (
  <div>
    <div className="eyebrow mb-3">{title}</div>
    <ul className="space-y-2 text-sm">
      {links.map((l) => (
        <li key={l.label}>
          {l.to ? (
            <Link to={l.to} className="link-underline hover:text-[#ff4500]">
              {l.label}
            </Link>
          ) : (
            <a href={l.href} className="link-underline hover:text-[#ff4500]">
              {l.label}
            </a>
          )}
        </li>
      ))}
    </ul>
  </div>
);
